/**
 * Helpers réutilisables pour la gestion des formulaires Element Plus
 * Centralise les fonctions communes utilisées dans les dialogs
 */

import { ElMessageBox } from 'element-plus';

// ============================================================================
// GESTION DES ERREURS
// ============================================================================

/**
 * Récupère le message d'erreur pour un champ depuis les erreurs backend
 * @param {string} field - Nom du champ
 * @param {Object|Array} errors - Objet ou tableau d'erreurs du backend
 * @returns {string} Message d'erreur ou chaîne vide
 */
export const getErrorForField = (field, errors) => {
  if (!errors) return '';

  // Format objet { field: ['error1', 'error2'] }
  if (errors[field]) {
    return Array.isArray(errors[field])
      ? errors[field].join(' ')
      : errors[field];
  }

  return '';
};

/**
 * Extrait les erreurs d'une réponse API Axios
 * @param {Error} error - Erreur Axios
 * @returns {Object} Objet d'erreurs par champ
 */
export const extractApiErrors = (error) => {
  if (error?.response?.data?.errors) {
    return error.response.data.errors;
  }
  if (error?.response?.data?.message) {
    return { _general: [error.response.data.message] };
  }
  return {};
};

/**
 * Vérifie si une réponse API contient des erreurs de validation
 * @param {Error} error - Erreur Axios
 * @returns {boolean}
 */
export const hasValidationErrors = (error) => {
  return error?.response?.status === 422;
};

/**
 * Extrait un message d'erreur humain depuis une erreur Axios, en gérant les
 * principaux formats que renvoie l'API : Laravel validation
 * ({ message, errors: { field: [...] } }), `{ message }`, `{ error }`, ou
 * chaîne brute. Retourne `fallback` si rien d'exploitable n'est trouvé.
 * Utile pour remplacer les `$message.error('Erreur...')` génériques qui
 * masquent le vrai message backend (permissions, validations métier, etc.).
 */
export const extractApiErrorMessage = (error, fallback = 'Une erreur est survenue') => {
  const data = error?.response?.data;
  if (!data) return fallback;

  // Format Laravel validation { errors: { field: [...] } }
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const flat = Object.values(data.errors).flat();
    if (flat.length > 0) return flat.join(' ');
  }
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors.join(' ');
  }

  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (typeof data === 'string' && data) return data;

  return fallback;
};

/**
 * Extrait les métadonnées d'erreur structurées de l'API : message humain,
 * code applicatif (`ERR-XXX-NNN`) et `correlation_id` injecté par le
 * middleware AssignCorrelationId.
 *
 * Le `correlation_id` est récupéré en priorité depuis le body de la réponse
 * (`data.correlation_id`), puis en fallback sur le header `X-Correlation-Id`
 * que le middleware ajoute à toute réponse.
 *
 * Retourne `{ message, code, correlationId }`. Aucune valeur n'est jamais
 * undefined : `code` et `correlationId` valent `null` si absents, ce qui
 * permet à `formatErrorToast()` de n'afficher que ce qui est connu.
 */
export const extractApiErrorDetails = (error, fallback = 'Une erreur est survenue') => {
  const response = error?.response;
  const data = response?.data;

  const code = (data && typeof data.code === 'string' && data.code) || null;
  const correlationId =
    (data && typeof data.correlation_id === 'string' && data.correlation_id) ||
    response?.headers?.['x-correlation-id'] ||
    null;

  return {
    message: extractApiErrorMessage(error, fallback),
    code,
    correlationId,
  };
};

/**
 * Construit le message d'erreur à afficher dans un toast : message principal
 * + ligne secondaire « ERR-XXX-NNN · réf : abc12 » quand on a ces infos.
 *
 * Utilisé conjointement avec `$message.error(formatErrorToast(error, '...'))`
 * pour remplacer les anciens `$message.error('Erreur lors de ...')` génériques.
 */
export const formatErrorToast = (error, fallback = 'Une erreur est survenue') => {
  const { message, code, correlationId } = extractApiErrorDetails(error, fallback);
  const parts = [];
  if (code) parts.push(code);
  if (correlationId) parts.push(`réf : ${correlationId}`);
  if (parts.length === 0) return message;
  return `${message}\n${parts.join(' · ')}`;
};

// ============================================================================
// GESTION DES FORMULAIRES
// ============================================================================

/**
 * Réinitialise un formulaire Element Plus
 * @param {Object} formRef - Référence au formulaire ($refs.formName)
 * @param {Object} additionalState - État supplémentaire à réinitialiser
 */
export const resetForm = (formRef, additionalState = {}) => {
  if (formRef) {
    formRef.resetFields();
    formRef.clearValidate();
  }

  // Réinitialiser l'état supplémentaire
  Object.keys(additionalState).forEach((key) => {
    additionalState[key] = typeof additionalState[key] === 'boolean' ? false : '';
  });
};

/**
 * Valide un formulaire de manière asynchrone
 * @param {Object} formRef - Référence au formulaire
 * @returns {Promise<boolean>}
 */
export const validateForm = async (formRef) => {
  if (!formRef) return false;

  try {
    await formRef.validate();
    return true;
  } catch {
    return false;
  }
};

/**
 * Valide un formulaire avec callback (style Element Plus classique)
 * @param {Object} formRef - Référence au formulaire
 * @param {Function} onValid - Callback si valide
 * @param {Function} onInvalid - Callback si invalide (optionnel)
 */
export const validateFormCallback = (formRef, onValid, onInvalid = null) => {
  if (!formRef) {
    if (onInvalid) onInvalid();
    return;
  }

  formRef.validate((valid) => {
    if (valid) {
      onValid();
    } else if (onInvalid) {
      onInvalid();
    }
  });
};

// ============================================================================
// GESTION DES DIALOGS
// ============================================================================

/**
 * Gère la fermeture d'un dialog avec confirmation si modifications
 * @param {Object} options - Options de configuration
 * @param {boolean} options.hasChanges - Y a-t-il des modifications non sauvegardées ?
 * @param {Function} options.onClose - Callback de fermeture
 * @param {string} options.confirmMessage - Message de confirmation (optionnel)
 * @param {string} options.confirmButtonText - Texte du bouton de confirmation
 * @param {string} options.cancelButtonText - Texte du bouton d'annulation
 */
export const handleDialogClose = async ({
  hasChanges = false,
  onClose,
  confirmMessage = 'Des modifications ont été apportées, quitter quand même ?',
  confirmButtonText = 'Quitter',
  cancelButtonText = 'Ne pas quitter'
}) => {
  if (!hasChanges) {
    onClose();
    return;
  }

  try {
    await ElMessageBox.confirm(confirmMessage, 'Confirmation', {
      distinguishCancelAndClose: true,
      confirmButtonText,
      cancelButtonText,
      type: 'warning'
    });
    onClose();
  } catch {
    // L'utilisateur a annulé, on ne fait rien
  }
};

/**
 * Crée un handler de fermeture pour un dialog
 * @param {Function} getHasChanges - Fonction qui retourne true si modifications
 * @param {Function} closeDialog - Fonction de fermeture du dialog
 * @returns {Function} Handler de fermeture
 */
export const createCloseHandler = (getHasChanges, closeDialog) => {
  return () => {
    handleDialogClose({
      hasChanges: getHasChanges(),
      onClose: closeDialog
    });
  };
};

// ============================================================================
// FORMATAGE DES DONNÉES
// ============================================================================

/**
 * Formate une date pour l'affichage (DD/MM/YYYY)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date formatée ou chaîne vide
 */
export const formatDateDisplay = (date) => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Formate une date pour l'API (YYYY-MM-DD)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date formatée ou null
 */
export const formatDateApi = (date) => {
  if (!date) return null;

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${year}-${month}-${day}`;
};

/**
 * Parse une date depuis le format DD/MM/YYYY
 * @param {string} dateStr - Date au format DD/MM/YYYY
 * @returns {Date|null}
 */
export const parseDateFromDisplay = (dateStr) => {
  if (!dateStr) return null;

  const pattern = /(\d{2})\/(\d{2})\/(\d{4})/;
  const match = dateStr.match(pattern);

  if (!match) return null;

  const [, day, month, year] = match;
  const date = new Date(year, month - 1, day);

  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formate un montant en euros
 * @param {number|string} amount - Montant à formater
 * @param {number} decimals - Nombre de décimales (défaut: 2)
 * @returns {string} Montant formaté
 */
export const formatCurrency = (amount, decimals = 2) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '0,00 €';

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/**
 * Parse un montant depuis une chaîne formatée
 * @param {string} amountStr - Montant formaté
 * @returns {number}
 */
export const parseCurrency = (amountStr) => {
  if (!amountStr) return 0;

  // Remplacer les espaces et le symbole €, convertir virgule en point
  const cleaned = String(amountStr)
    .replace(/[€\s]/g, '')
    .replace(',', '.');

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// ============================================================================
// HELPERS DE SOUMISSION
// ============================================================================

/**
 * Wrapper pour la soumission de formulaire avec gestion d'état
 * @param {Object} options - Options de configuration
 * @param {Object} options.formRef - Référence au formulaire
 * @param {Function} options.submitFn - Fonction de soumission async
 * @param {Function} options.onSuccess - Callback en cas de succès
 * @param {Function} options.onError - Callback en cas d'erreur
 * @param {Function} options.setLoading - Fonction pour définir l'état de chargement
 * @param {Function} options.setErrors - Fonction pour définir les erreurs
 */
export const submitFormWithState = async ({
  formRef,
  submitFn,
  onSuccess,
  onError,
  setLoading,
  setErrors
}) => {
  // Valider le formulaire
  const isValid = await validateForm(formRef);
  if (!isValid) return;

  setLoading(true);
  setErrors({});

  try {
    const result = await submitFn();
    if (onSuccess) onSuccess(result);
  } catch (error) {
    const errors = extractApiErrors(error);
    setErrors(errors);
    if (onError) onError(error, errors);
  } finally {
    setLoading(false);
  }
};

// ============================================================================
// HELPERS POUR LISTES/OPTIONS
// ============================================================================

/**
 * Transforme un tableau d'objets en options pour el-select
 * @param {Array} items - Tableau d'objets
 * @param {string} labelKey - Clé pour le label
 * @param {string} valueKey - Clé pour la valeur
 * @returns {Array} Options formatées
 */
export const toSelectOptions = (items, labelKey = 'name', valueKey = 'id') => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    label: item[labelKey],
    value: item[valueKey]
  }));
};

/**
 * Trouve un élément dans une liste par sa valeur
 * @param {Array} items - Tableau d'objets
 * @param {*} value - Valeur à chercher
 * @param {string} valueKey - Clé de la valeur
 * @returns {Object|null}
 */
export const findByValue = (items, value, valueKey = 'id') => {
  if (!Array.isArray(items)) return null;
  return items.find((item) => item[valueKey] === value) || null;
};

// ============================================================================
// EXPORT PAR DÉFAUT
// ============================================================================

export default {
  // Gestion des erreurs
  getErrorForField,
  extractApiErrors,
  extractApiErrorMessage,
  extractApiErrorDetails,
  formatErrorToast,
  hasValidationErrors,

  // Gestion des formulaires
  resetForm,
  validateForm,
  validateFormCallback,

  // Gestion des dialogs
  handleDialogClose,
  createCloseHandler,

  // Formatage
  formatDateDisplay,
  formatDateApi,
  parseDateFromDisplay,
  formatCurrency,
  parseCurrency,

  // Soumission
  submitFormWithState,

  // Listes/Options
  toSelectOptions,
  findByValue
};
